/**
 * PR-K.1 — shared camera snapshot bridge between R3F (`<Canvas>`) and
 * sibling DOM overlays.
 *
 * # Why this module exists
 *
 * R3F's reconciler treats every React child of `<Canvas>` as a candidate
 * THREE.js primitive. `ReactDOM.createPortal` returning a `<div>` tree
 * crashes the reconciler with `R3F: Div is not part of the THREE
 * namespace!` (PR-K.1 hotfix `b8f7689`).
 *
 * The fix is to keep DOM overlays **outside** `<Canvas>` (as siblings,
 * not children) but still drive their layout from the live THREE
 * camera. This module is the bridge:
 *
 *   - A camera-tracker component **inside** `<Canvas>` calls
 *     `setCameraSnapshot(camera, size)` from `useFrame` on every
 *     frame.
 *   - A DOM overlay **outside** `<Canvas>` calls
 *     `projectWorldToClient(x, y)` to translate world coords to client
 *     pixels at render time. It can also `subscribe(listener)` to
 *     re-render when the camera updates.
 *
 * The snapshot is module-level (not React state) on purpose:
 *   - `useFrame` runs at the THREE render cadence; converting it to
 *     React state on every frame would burn ~16 ms of reconciliation.
 *   - The version counter lets DOM consumers throttle to camera-change
 *     events via `useSyncExternalStore` (or our `useCameraVersion`
 *     hook) instead of polling.
 *
 * # Coordinate convention
 *
 * Clients must speak the same coordinate space as the THREE scene:
 * world coords *after* the coordinate-origin offset has been applied
 * (see `apps/web/src/app/scene3d/coordinateOrigin.ts`). Cursor-derived
 * overlays (DynInput, live-text) already get post-offset coords straight
 * from `cam.unproject`, so they project as-is. Overlays fed from the
 * DOCUMENT-frame data instead carry raw WCS (the entity-preview string bag
 * and the cad-core typed grips are both true DXF coords), so they MUST
 * `applyOffset` before calling `projectWorldToClient` — the grips overlay
 * does this at its single projection choke-point (`gripsOverlay.tsx`'s
 * `projected` map). Without it a non-zero-origin (geodetic / UTM) import
 * projects those overlays ~|origin| off the rendered geometry (Codex S12b P2).
 */

import * as THREE from "three";

/**
 * Snapshot of the THREE camera + canvas dimensions captured during
 * `useFrame`. `null` until the first frame has rendered.
 */
export interface CameraSnapshot {
  /** Camera reference — re-used across frames; do not mutate. */
  readonly camera: THREE.Camera;
  /** Canvas size in CSS pixels. */
  readonly size: { readonly width: number; readonly height: number };
}

/**
 * Module-level mutable holder. Pointer comparison is intentional so a
 * subscriber can detect "still pointing at the same camera, only
 * matrices changed" via the version counter.
 */
const state = {
  snapshot: null as CameraSnapshot | null,
  version: 0,
  listeners: new Set<() => void>(),
};

/**
 * Reusable scratch vector for `projectWorldToClient`. Avoids allocating
 * a new `THREE.Vector3` per call (each grip projection is a hot path
 * during pan / zoom).
 */
const scratch = new THREE.Vector3();

/**
 * Update the active camera snapshot and notify subscribers. Called from
 * inside `useFrame` by the camera-tracker components.
 *
 * Phase 1.k (П35Р React #185 fix) — bulk-load text mesh build invokes
 * `invalidate()` many times in tight succession. Each invalidate fires
 * `useFrame` which calls this function; bumping `version` + notifying
 * `useSyncExternalStore` listeners synchronously inside a React commit
 * phase makes USS detect tearing on every subscriber, looping
 * forceStoreRerender past React's 50-update budget → error #185.
 *
 * The fix coalesces the listener notification onto the next animation
 * frame. The snapshot itself stays current (callers reading via
 * `getCameraSnapshot()` see the latest data), only the React-facing
 * subscribers are notified at most once per rAF.
 */
let _notifyScheduled = false;
function _flushListeners(): void {
  _notifyScheduled = false;
  for (const listener of state.listeners) listener();
}
export function setCameraSnapshot(
  camera: THREE.Camera,
  size: { width: number; height: number },
): void {
  state.snapshot = { camera, size: { width: size.width, height: size.height } };
  state.version += 1;
  if (_notifyScheduled) return;
  _notifyScheduled = true;
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(_flushListeners);
  } else {
    queueMicrotask(_flushListeners);
  }
}

/** Read the current snapshot. `null` if no frame has rendered yet. */
export function getCameraSnapshot(): CameraSnapshot | null {
  return state.snapshot;
}

/**
 * Monotonic version counter — increments every time
 * `setCameraSnapshot` is called. DOM components subscribe to this via
 * `useSyncExternalStore` so they re-render only when the camera
 * actually changed (cheap pointer-equality comparison vs. matrix-hash
 * inside `useFrame`).
 */
export function getCameraVersion(): number {
  return state.version;
}

/**
 * Register a listener fired on every `setCameraSnapshot`. Returns a
 * cleanup function that removes the listener. Designed to slot into
 * `useSyncExternalStore`'s subscribe contract.
 */
export function subscribeCameraSnapshot(listener: () => void): () => void {
  state.listeners.add(listener);
  return () => {
    state.listeners.delete(listener);
  };
}

/**
 * Project a world-space point to canvas-relative client pixels using
 * the current camera snapshot. Returns `null` if no frame has rendered
 * yet (initial mount, before R3F's first `useFrame` tick).
 *
 * Pure function on the snapshot — safe to call from React render
 * (no side effects, no THREE-state mutation beyond the scratch vec).
 */
export function projectWorldToClient(
  worldX: number,
  worldY: number,
): { x: number; y: number } | null {
  const snap = state.snapshot;
  if (!snap) return null;
  scratch.set(worldX, worldY, 0);
  scratch.project(snap.camera);
  const cx = ((scratch.x + 1) / 2) * snap.size.width;
  const cy = ((-scratch.y + 1) / 2) * snap.size.height;
  return { x: cx, y: cy };
}

/**
 * Test-only reset hook. Clears the snapshot, version, and listeners so
 * unit tests can run in isolation. Production code should never call
 * this — listeners would leak on a hot-reload otherwise.
 */
export function __resetCameraSnapshotForTests(): void {
  state.snapshot = null;
  state.version = 0;
  state.listeners.clear();
}
